import React, { useState, useEffect } from "react";
import {
  Button,
  Spinner,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import DataTable from "react-data-table-component";
import {
  AddExpenseTitle,
  EditExpenseTitle,
  ExpenseTitle,
} from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { Expense } from "../../../Types/Tables.type";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn } from "../../../AbstractElements";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import useCustomToasts from "../../../hooks/useCustomToast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDebouncedCallback } from "use-debounce";
import { useAuth } from "../../../Context/AuthContext";

interface FormValues {
  title: string;
  description?: string;
  amount: number;
  date: Date;
}
interface ErrorResponse {
  message: string;
}

const formatDate = (date: Date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function ViewExpense() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Expense[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const { currency } = useCurrency();
  const { hasPermission } = useAuth();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: "",
      description: "",
      amount: "" as any,
      date: undefined as any,
    },
  });

  const fetchData = async (
    page = currentPage,
    currentPerPage = perPage,
    search = filterText,
    sortField = sortBy,
    sortDir = sortOrder,
    silent = false
  ) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${baseUrl}expense/get_all_paginated`, {
        params: {
          page,
          per_page: currentPerPage,
          search: search.trim() !== "" ? search : undefined,
          sort_by: sortField !== "" ? sortField : undefined,
          sort_order: sortDir,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && Array.isArray(response.data.data)) {
        setFilteredItems(response.data.data);
        setTotalRows(response.data.total);
        setCurrentPage(response.data.current_page);
      } else if (Array.isArray(response.data)) {
        setFilteredItems(response.data);
        setTotalRows(response.data.length);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage, filterText, sortBy, sortOrder);
  }, [refresh]);

  useEffect(() => {
    if (location.state?.id) {
      setEditingId(location.state.id);
      const fetchExpenseById = async () => {
        try {
          const result = await axios.post(
            `${baseUrl}expense/viewbyid`,
            { id: location.state.id },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setValue("title", result.data.title);
          setValue("description", result.data.description || "");
          setValue("amount", Number(result.data.amount));
          setValue("date", new Date(result.data.date));
        } catch (error) {
          console.error("Error fetching expense details:", error);
        }
      };
      fetchExpenseById();
    }
  }, [location.state?.id, setValue, token]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, filterText, sortBy, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, filterText, sortBy, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "title";
    setSortBy(sortField);
    setSortOrder(sortDirection as "asc" | "desc");
    fetchData(1, perPage, filterText, sortField, sortDirection as "asc" | "desc");
  };

  const debouncedFetch = useDebouncedCallback(
    (searchVal: string, pageVal: number, perPageVal: number, sortVal: string, sortDirVal: "asc" | "desc") => {
      fetchData(pageVal, perPageVal, searchVal, sortVal, sortDirVal);
    },
    400
  );

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilterText(value);
    setCurrentPage(1);
    debouncedFetch(value, 1, perPage, sortBy, sortOrder);
  };

  const handleEdit = (row: Expense) => {
    setEditingId(row.id);
    setValue("title", row.title);
    setValue("description", row.description || "");
    setValue("amount", Number(row.amount));
    setValue("date", new Date(row.date));
    clearErrors();
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    reset({ title: "", description: "", amount: "" as any, date: undefined as any });
    clearErrors();
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this expense?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button color="danger" onClick={() => confirmDelete(id)}>
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}expense/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`Expense Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setFilteredItems((prevItems) =>
          prevItems.filter((item) => item.id !== id)
        );
        if (editingId === id) {
          handleCancelEdit();
        }
        fetchData(currentPage, perPage, filterText, sortBy, sortOrder, true);
      } catch (error) {
        console.error("Error deleting expense:", error);
        toast.error("An error occurred while deleting the expense", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  const formatAmount = (value: number | string): string => {
    const num = parseFloat(value.toString());
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
  };

  const expenseTableColumns = [
    {
      name: "Sl. No.",
      cell: (_row: Expense, index: number) =>
        (currentPage - 1) * perPage + index + 1,
      width: "12%",
    },
    {
      name: "Title",
      selector: (row: Expense) => row.title,
      sortable: true,
      sortField: "title",
      width: "25%",
      wrap: true
    },
    {
      name: "Date",
      selector: (row: Expense) => row.date.split(" ")[0],
      sortable: true,
      sortField: "date",
      width: "20%",
    },
    {
      name: `Amount (${currency || "₹"})`,
      selector: (row: Expense) => formatAmount(row.amount),
      sortable: true,
      sortField: "amount",
      width: "23%",
    },
    {
      name: "Actions",
      cell: (row: Expense) => (
        <div className="d-flex justify-content-between">
          {hasPermission("expense.edit") && (
            <Button
              color="primary"
              size="sm"
              onClick={() => handleEdit(row)}
              style={{
                marginRight: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Edit"
            >
              <i className="icon-pencil-alt" />
            </Button>
          )}
          {hasPermission("expense.delete") && (
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDelete(row.id)}
              disabled={disabledDelete === row.id || disableAllDelete}
              style={{
                marginRight: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Delete"
            >
              <i className="icon-trash" />
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "20%",
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const formattedData = {
        ...data,
        date: formatDate(data?.date),
      };

      if (!editingId) {
        // Add Mode
        const response = await axios.post(
          `${baseUrl}expense/store`,
          formattedData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          reset({ title: "", description: "", amount: "" as any, date: undefined as any });
          toast.success(`Expense added successfully`, {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
          });
          setCurrentPage(1);
          fetchData(1, perPage, filterText, sortBy, sortOrder, true);
        } else {
          ErrorToast("Failed to add expense");
        }
      } else {
        // Edit Mode
        const editPayload = {
          ...formattedData,
          id: editingId,
        };

        const response = await axios.post(
          `${baseUrl}expense/update`,
          editPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          reset({ title: "", description: "", amount: "" as any, date: undefined as any });
          setEditingId(null);
          toast.success(`Expense updated successfully`, {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
          });
          fetchData(currentPage, perPage, filterText, sortBy, sortOrder, true);
        } else {
          ErrorToast("Failed to update expense");
        }
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 1000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={editingId ? EditExpenseTitle : AddExpenseTitle}
        parent={ExpenseTitle}
        title={editingId ? EditExpenseTitle : AddExpenseTitle}
      />
      <Row>
        {(hasPermission("expense.add") || (editingId && hasPermission("expense.edit"))) && (
          <Col xl={hasPermission("expense.view") ? 4 : 12} className="mb-lg-3">
            <Card>
              <CardBody className="custom-input form-validation">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    {editingId ? EditExpenseTitle : AddExpenseTitle}
                  </h5>
                  {editingId && (
                    <span className="badge bg-warning text-dark">Edit Mode</span>
                  )}
                </div>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Label htmlFor="expense-title">
                        Title <small className="text-danger">*</small>
                      </Label>
                      <Controller
                        name="title"
                        control={control}
                        rules={{ required: "Title is required" }}
                        render={({ field }) => (
                          <>
                            <Input
                              id="expense-title"
                              type="text"
                              placeholder="Expense Title"
                              {...field}
                              invalid={!!errors.title}
                              onKeyDown={handleKeyDown}
                              style={{
                                color: "var(--input-text-color)",
                                borderColor: errors.title ? "red" : "",
                              }}
                            />
                            {errors.title && (
                              <FormFeedback>{errors.title.message}</FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </Col>

                    <Col xs={12}>
                      <Label htmlFor="expense-description">Description</Label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <Input
                            id="expense-description"
                            type="textarea"
                            placeholder="Description"
                            {...field}
                            value={field.value ?? ""}
                            onKeyDown={handleKeyDown}
                          />
                        )}
                      />
                    </Col>

                    <Col xs={12}>
                      <Label htmlFor="expense-amount">
                        Amount({currency}) <small className="text-danger">*</small>
                      </Label>
                      <Controller
                        name="amount"
                        control={control}
                        rules={{
                          required: "Amount is required",
                          validate: (value) =>
                            value > 0 || "Amount must be greater than 0",
                        }}
                        render={({ field }) => (
                          <>
                            <Input
                              id="expense-amount"
                              type="number"
                              placeholder="Amount"
                              {...field}
                              value={field.value ?? ""}
                              invalid={!!errors.amount}
                              onKeyDown={(e) => {
                                handleKeyDown(e);
                                handleAmountKeyDown(e);
                              }}
                              style={{
                                color: "var(--input-text-color)",
                                borderColor: errors.amount ? "red" : "",
                              }}
                              onWheel={(e) =>
                                (e.target as HTMLInputElement).blur()
                              }
                            />
                            {errors.amount && (
                              <FormFeedback>{errors.amount.message}</FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </Col>

                    <Col xs={12}>
                      <Label htmlFor="expense-date">
                        Date <small className="text-danger">*</small>
                      </Label>
                      <Controller
                        name="date"
                        control={control}
                        rules={{ required: "Date is required" }}
                        render={({ field }) => (
                          <>
                            <DatePicker
                              selected={field.value}
                              onChange={(date) => field.onChange(date)}
                              placeholderText="Select date"
                              className={`form-control custom-datepicker ${errors.date ? "is-invalid" : ""
                                }`}
                              dateFormat="dd-MM-yyyy"
                              maxDate={new Date()}
                            />
                            {errors.date && (
                              <div className="invalid-feedback d-block">
                                {errors.date.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Col>

                    <Col xs={12} className="mt-3 d-flex gap-2">
                      <Btn color="primary" disabled={isButtonDisabled}>
                        {editingId ? "Update" : "Submit"}
                      </Btn>
                      {editingId && (
                        <Button
                          type="button"
                          color="secondary"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        )}
        {hasPermission("expense.view") && (
          <Col xl={hasPermission("expense.add") || hasPermission("expense.edit") ? 8 : 12} className="mb-lg-3">
            <Card>
              <CardBody>
                <div className="mb-3">
                  <FilterComponent
                    onFilter={handleFilter}
                    filterText={filterText}
                  />
                </div>
                <div className="table-responsive">
                  <DataTable
                    data={filteredItems}
                    columns={expenseTableColumns}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    paginationDefaultPage={currentPage}
                    sortServer
                    onSort={handleSort}
                    progressPending={loading}
                    progressComponent={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "200px",
                          width: "100%",
                          padding: "20px 0",
                        }}
                      >
                        <Spinner color="primary" />
                      </div>
                    }
                    highlightOnHover
                    striped
                    customStyles={customStyles}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>
      <ToastContainer />
    </>
  );
}
